import React, { useState, useEffect } from 'react';
import { coreAPI } from '../../services/api';
import { useOrganization } from '../../context/OrganizationContext';
import { LoadingPage } from '../../components/Loading';
import './OrganizationStructure.css';

const OrganizationStructure = () => {
  const { organization } = useOrganization();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch organization members only
      const membersResponse = await coreAPI.getOrganizationMembers();
      // Handle both paginated and non-paginated responses
      const membersData = membersResponse.data.results || membersResponse.data;
      setMembers(Array.isArray(membersData) ? membersData : []);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('無法載入組織架構內容');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <div className="organization-structure">
        <div className="container">
          <div className="error-message">
            <h2>{error}</h2>
            <p>請返回首頁或聯絡我們。</p>
          </div>
        </div>
      </div>
    );
  }

  const organizationName = organization?.chinese_name || organization?.english_name || 'Hong Kong Synergies';

  return (
    <div className="organization-structure">
      <div className="org-container">
        {/* Hero/Title Area */}
        <div className="org-hero">
          <h1 className="org-title">
            {organizationName} 組織架構
          </h1>
          <p className="org-subtitle">{organization?.structure_subtitle || '攜手共建更美好的香港'}</p>
        </div>

        {/* Main Content Grid */}
        {members && members.length > 0 ? (
          <div className="members-grid">
            {members.map((member) => (
              <div key={member.id} className="member-card">
                <div className="member-portrait">
                  {member.photo ? (
                    <img src={member.photo} alt={member.name} />
                  ) : (
                    <div className="member-avatar-placeholder">
                      {member.name.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="member-details">
                  <h2 className="member-name">{member.name}</h2>
                  <p className="member-position">{member.position_name}</p>
                  {member.description && (
                    <div 
                      className="member-description"
                      dangerouslySetInnerHTML={{ __html: member.description }}
                    />
                  )}
                  {(member.facebook_url || member.instagram_url) && (
                    <div className="member-social">
                      {member.facebook_url && (
                        <a 
                          href={member.facebook_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="social-icon facebook"
                          aria-label="Facebook"
                        >
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                          </svg>
                        </a>
                      )}
                      {member.instagram_url && (
                        <a 
                          href={member.instagram_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="social-icon instagram"
                          aria-label="Instagram"
                        >
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
                          </svg>
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-members">
            <p>暫無組織成員資料</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrganizationStructure;
